/* General */
import Vue from 'vue'
import { clone, cloneDeep, mapKeys, throttle, isEqual } from 'lodash'

/* Mixins */

/* Components */

/* Helpers */
import LocalStore from '@/helpers/localstore'
import ItemFilterAssistant from '@/helpers/item-filter-assistant'
import TextAssistant from '@/helpers/text-assistant'

/* Data types */
import {
  EEntities,
  DefaultFilterIDs,
  SortOptionValues,
  DueTypeValues,
  StatusTypeValues,
  DefaultFilterNavItemsOrder,
  DefaultNamedFilterIcons,
} from '@/types'
import { INavMenuItem } from '@/types/data-objects/global'
import { IHomeTableItem } from '@/types/data-objects/items'
import {
  IItemTraitSet,
  ICustomFilterItemTraitSet,
  DefaultItemTraitSet,
} from '@/types/data-objects/listing'
import {
  IConvosOutputAllFilterOptions,
  ITasksOutputAllFilterOptions,
  IItemForSelection,
  IPersonBase,
  IPriorityLevelForSelection,
  IStatusForSelection,
} from '@/api/types/response.types'

/* API */

/* Constants */

export default Vue.extend({
  data() {
    return {
      EEntities,
      ItemFilterAssistant,

      // Override in parent
      isAllConvos: false,

      totalItemCount: 0,
      itemTraitSet: cloneDeep(DefaultItemTraitSet),
      itemsPerPage: 50,
      currentPage: 1,
      currentFilterNavItem: ItemFilterAssistant.getEmptyFilterNavItem(),

      /* Shared filter options for listing */
      defaultFilterNavItems: [] as INavMenuItem[],
      customFilterNavItems: [] as INavMenuItem[],
      tags: [] as IItemForSelection[],
      dueDateOptions: [] as IItemForSelection[],
      priorities: [] as IPriorityLevelForSelection[],
      activeStatuses: [] as IStatusForSelection[],
      inactiveStatuses: [] as IStatusForSelection[],
      customFilterItemTraitSets: [] as ICustomFilterItemTraitSet[],
      assignees: [] as IPersonBase[],

      entities: [] as IItemForSelection[],
      jobs: [] as IItemForSelection[],

      /* Polling */
      lastAccessEpochTime: null,
      poller: null,
      pollIntervalInMS: this.$store.get('app/autoRefreshDataPingInMS'),

      // UI
      isLoadingInitialData: true,
      // Small period of async time before first results are fetched.
      // Parent created() has async calls that need to load first so immediately
      // set loading results to true now (otherwise "empty state" shows)
      isLoadingResults: true,
      fetchedInitialResults: false,
      isCustomFilterSubmitting: false,

      showMoveItemsModal: false,
      showAddCommentModal: false,

      results: [] as IHomeTableItem[],
      selectedItemIDs: [] as number[],

      throttleFetchResults: null,
    }
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    /* Gets a distinct list of entity IDs from all selected tasks/convos */
    entityIDsForSelectedRows(): number[] {
      if (!this.hasSelectedRows) {
        return []
      }

      const entityIDs = this.results
        .filter(r => this.selectedItemIDs.includes(r.id))
        .map(r => r.parent.id)
        .sort()

      /* Remove duplicate IDs using Set() */
      return [...new Set(entityIDs)]
    },
    hasEntities(): boolean {
      return this.entities.length > 0
    },
    hasSelectedRows(): boolean {
      return this.selectedItemIDs.length > 0
    },
    hasPages(): boolean {
      return this.totalItemCount > this.itemsPerPage
    },
    hasResults(): boolean {
      return this.results.length > 0
    },
    isDefaultItemTraitSet(): boolean {
      return this.isItemTraitSetEquivalent(DefaultItemTraitSet)
    },
  },

  watch: {
    itemTraitSet: {
      deep: true,
      async handler(val) {
        // On any active filter update after the initial fetch, default page back to 1
        if (this.fetchedInitialResults) {
          this.currentPage = 1
        }
        this.syncCurrentFilterNavItemWithItemTraitSet()
        await this.updateURLAndFetchResults()
      },
    },
    defaultFilterNavItems: {
      handler() {
        this.syncCurrentFilterNavItemWithItemTraitSet()
      },
    },
    customFilterNavItems: {
      handler() {
        this.syncCurrentFilterNavItemWithItemTraitSet()
      },
    },
  },

  methods: {
    // Explicitly call this at end of created() method.
    // Specifically, requires custom filter loaded so an initial querystring value of 'custom_filter_id'
    // will find the corresponding custom filter to hydrate the itemTraitSet with
    postCreated() {
      this.throttleFetchResults = throttle(this.fetchResults, 1000)
      this.updateItemTraitSetFromURLQueryParams()
    },
    isItemTraitSetEquivalent(val: IItemTraitSet): boolean {
      // For the purposes of "equivalency", we will not consider the sort...
      const sortlessItemTraitSet = cloneDeep(this.itemTraitSet)
      sortlessItemTraitSet.sort = SortOptionValues.UPDATED_RECENT

      const sortlessVal = cloneDeep(val)
      sortlessVal.sort = SortOptionValues.UPDATED_RECENT

      return isEqual(sortlessVal, sortlessItemTraitSet)
    },

    hydrateFilterOptionsForListing_Base(
      data: IConvosOutputAllFilterOptions | ITasksOutputAllFilterOptions,
    ) {
      const defFilters = data.listDefaultFilters.map(item => ({
        value: ItemFilterAssistant.getDefaultFilterIdentifier(item.id),
        label: item.name,
        count: null, // Not evaluating from backend for now, let's just hide.
        icon: DefaultNamedFilterIcons.find(
          (filter: any) => filter.label === item.name,
        ).icon,
        indented: DefaultNamedFilterIcons.find(
          (filter: any) => filter.label === item.name,
        ).indented,
      }))

      this.defaultFilterNavItems = this.mapOrder(
        defFilters,
        DefaultFilterNavItemsOrder,
        'label',
      )

      this.tags = data.listTags
      this.priorities = data.listPriorities
      this.activeStatuses = data.listActiveStatuses
      this.inactiveStatuses = data.listInactiveStatuses
      this.dueDateOptions = data.listDueDateRanges
      this.assignees = data.listAssignees
      this.jobs = data.listJobs
    },

    mapOrder(array: any, order: any, key: string) {
      array.sort((a: any, b: any) => {
        const A = a[key]
        const B = b[key]
        if (order.indexOf(A) > order.indexOf(B)) {
          return 1
        } else {
          return -1
        }
      })
      return array
    },

    async fetchResults() {
      // Placeholder, this method should be implemented in screen
      // that uses this mixin (mailbox or project home for example)
    },

    // Updates URL based on current filter data and fetches results if URL has changed
    async updateURLAndFetchResults() {
      const currentPath = this.$route.fullPath
      this.updateURLQueryParamsFromTraitSet(this.itemTraitSet, this.currentPage)
      const newPath = this.$route.fullPath

      // Only fetch results if this is the first time or if the route path has changed.
      // This ensures any itemTrait updates that did not result in a meaningful change
      // do not have an extra call.
      if (!this.fetchedInitialResults || currentPath !== newPath) {
        if (this.isAllConvos) {
          LocalStore.setAllConvosSavedQuery(this.accountID, this.$route.query)
        } else {
          LocalStore.setAllTasksSavedQuery(this.accountID, this.$route.query)
        }
        this.fetchedInitialResults = true
        await this.throttleFetchResults()
      }
    },

    loadInitialDefaultFilter() {
      this.updateItemTraitSetFromFilterID(
        ItemFilterAssistant.DefaultInitialFilterIdentifier,
      )
    },

    /****************************************************/
    /* URL / ItemTraitSet syncing methods             
    /****************************************************/
    // Called on creation so ItemTraitSet matches the URL requested
    updateItemTraitSetFromURLQueryParams() {
      // If query has `default_filter_id`, update the item trait set from that...
      if (this.$route.query.default_filter_id) {
        this.updateItemTraitSetFromFilterID(
          ItemFilterAssistant.getDefaultFilterIdentifier(
            this.$route.query.default_filter_id,
          ),
        )
        return
      }

      // If query has `custom_filter_id`, update the item trait set from that...
      if (this.$route.query.custom_filter_id) {
        this.updateItemTraitSetFromFilterID(
          ItemFilterAssistant.getCustomFilterIdentifier(
            this.$route.query.custom_filter_id,
          ),
        )
        return
      }

      const paramsToFiltersMapper = {
        page: 'page',
        mailbox_ids: 'mailboxIDs',
        internal_project_ids: 'projectIDs',
        priority_ids: 'priorityIDs',
        status_ids: 'statusIDs',
        status_type: 'statusType',
        assignee_ids: 'assigneeIDs',
        assigned_not_to_you: 'assignedNotToYou',
        job_appointee_groupings_string: 'jobAppointeeGroupingsString',
        sort: 'sort',
        due_type: 'dueType',
        including_tag_ids: 'includingTagIDs',
        not_including_tag_ids: 'notIncludingTagIDs',
        start_due_date: 'startDueDate',
        end_due_date: 'endDueDate',
        search_term: 'searchTerm',
        only_watching: 'onlyWatching',
        created_within_days: 'createdWithinDays',
        last_updated_within_days: 'lastUpdatedWithinDays',
        creator_ids: 'creatorIDs',
        customer_ids: 'customerIDs',
        is_spam: 'isSpam',
      } as {
        [key: string]: string
      }

      const urlHasQueryFilterParams = this.queryHasFilterCriteria(
        Object.keys(paramsToFiltersMapper),
        this.$route.query,
      )

      let queryParams: Object = null
      let fromQueryString: boolean = false

      if (urlHasQueryFilterParams) {
        // Case 1: URL HAS QUERYSTRING PARAMS...
        // (a) Either accessed manually by user or...
        // (b) Set in this.$store for "back button" functionality on detail page
        queryParams = this.$route.query
        fromQueryString = true
      } else {
        // Case 2: URL IS DEFAULT BUT WE HAVE LOCAL STORAGE DATA...
        const storedPreviousQueryParams = this.isAllConvos
          ? LocalStore.getAllConvosSavedQuery(this.accountID)
          : LocalStore.getAllTasksSavedQuery(this.accountID)

        if (storedPreviousQueryParams) {
          try {
            queryParams = JSON.parse(storedPreviousQueryParams)

            // Always set the stored page back to 1 from localstorage saved session...
            // Perhaps eventually remove but then expose pagination at the top of the
            // list pages for clarity
            // @ts-ignore
            queryParams[paramsToFiltersMapper.page] = 1
          } catch (e) {
            this.isAllConvos
              ? LocalStore.clearAllConvosSavedQuery(this.accountID)
              : LocalStore.clearAllTasksSavedQuery(this.accountID)
          }
        }

        if (queryParams === null) {
          // Case 3: NO QUERYSTRING OR LOCAL STORAGE DATA (aka first time, or they manually cleared local storage).
          // Default the filters and return, but let's start them on 'active'
          this.itemTraitSet = cloneDeep(DefaultItemTraitSet)
          this.itemTraitSet.statusType = StatusTypeValues.ACTIVE
          return
        }
      }

      const newItemTraitSet: IItemTraitSet = cloneDeep(DefaultItemTraitSet)

      Object.keys(paramsToFiltersMapper).forEach(
        (key: string): void => {
          const curFilter = paramsToFiltersMapper[key]

          let value = DefaultItemTraitSet[curFilter]

          if (queryParams.hasOwnProperty(key)) {
            // @ts-ignore
            value = queryParams[key]
          }

          const isPage = curFilter === 'page'

          if (isPage) {
            this.currentPage = parseInt(value, 10)
          } else {
            if (fromQueryString) {
              this.updateItemTraitSetPropertyFromURLParam(
                newItemTraitSet,
                curFilter,
                value,
              )
            } else {
              newItemTraitSet[curFilter] = clone(value)
            }
          }
        },
      )

      // Finally, with the newItemTraitSet hydrated, let's "clean up" some filters, if necessary...

      // Due type (Pre- July 2022 legacy filters, due_type=-1 was the default, so this will clean transitioners as well)
      if (
        newItemTraitSet.dueType !== null &&
        newItemTraitSet.dueType !== DueTypeValues.ANYTIME &&
        newItemTraitSet.dueType !== DueTypeValues.DATE_RANGE &&
        newItemTraitSet.dueType !== DueTypeValues.DUE_NOW &&
        newItemTraitSet.dueType !== DueTypeValues.THIS_WEEK
      ) {
        newItemTraitSet.dueType = null
      }

      // Priorities (even though these aren't technically 'defined types'), let's treat 'em as such for now...
      if (newItemTraitSet.priorityIDs !== null) {
        newItemTraitSet.priorityIDs = newItemTraitSet.priorityIDs.filter(p =>
          [1, 2, 3, 4].includes(p),
        )
      }

      // Created within days must be null or between 0 and 180...
      if (isNaN(newItemTraitSet.createdWithinDays)) {
        newItemTraitSet.createdWithinDays = null
      } else if (newItemTraitSet.createdWithinDays) {
        if (newItemTraitSet.createdWithinDays > 180) {
          newItemTraitSet.createdWithinDays = 180
        } else if (newItemTraitSet.createdWithinDays <= 0) {
          newItemTraitSet.createdWithinDays = null
        }
      }

      // Last updated within days must be null or between 0 and 180...
      if (isNaN(newItemTraitSet.lastUpdatedWithinDays)) {
        newItemTraitSet.lastUpdatedWithinDays = null
      } else if (newItemTraitSet.lastUpdatedWithinDays) {
        if (newItemTraitSet.lastUpdatedWithinDays > 180) {
          newItemTraitSet.lastUpdatedWithinDays = 180
        } else if (newItemTraitSet.lastUpdatedWithinDays <= 0) {
          newItemTraitSet.lastUpdatedWithinDays = null
        }
      }

      this.itemTraitSet = newItemTraitSet
    },
    // Detects whether any search params exist in the current URL
    queryHasFilterCriteria(params: string[], queryParams: Object): boolean {
      return params.some((key: string) => {
        // @ts-ignore
        return queryParams[key] !== undefined
      })
    },
    // Does the 'casting' of filter values from its URL param representation.
    updateItemTraitSetPropertyFromURLParam(
      itemTraitSet: IItemTraitSet,
      filter: string,
      value: any,
    ) {
      const ARRAY_FILTERS = [
        'mailboxIDs',
        'projectIDs',
        'creatorIDs',
        'customerIDs',
        'priorityIDs',
        'statusIDs',
        'assigneeIDs',
        'includingTagIDs',
        'notIncludingTagIDs',
      ]
      const NUMBER_FILTERS = [
        'dueType',
        'mailboxIDs',
        'projectIDs',
        'creatorIDs',
        'customerIDs',
        'priorityIDs',
        'statusIDs',
        'assigneeIDs',
        'includingTagIDs',
        'notIncludingTagIDs',
        'createdWithinDays',
        'lastUpdatedWithinDays',
      ]
      const BOOLEAN_FILTERS = ['assignedNotToYou', 'isSpam', 'onlyWatching']

      const isNumber =
        NUMBER_FILTERS.includes(filter) &&
        typeof value === 'string' &&
        value !== undefined &&
        value !== null

      const isBoolean = BOOLEAN_FILTERS.includes(filter)

      const isArray =
        ARRAY_FILTERS.includes(filter) &&
        (typeof value === 'string' || typeof value === 'object')

      const isArrayOfStrings = isArray && typeof value === 'string'

      const isArrayOfNumbers = isArray && NUMBER_FILTERS.includes(filter)

      if (isArray) {
        if (isArrayOfStrings) {
          // Map comma-delimited string list from qs to an array...
          value = value.split(',')
        }
        if (isArrayOfNumbers) {
          // If numbers, then map the array elements to numbers...
          value = value.map((item: string) => parseInt(item, 10))
        }
      } else if (isNumber) {
        value = parseInt(value, 10)
      } else if (isBoolean) {
        value = value === 'true' // Explicitly only allow 'true' in qs for a true boolean value
      }

      // @ts-ignore
      itemTraitSet[filter] = clone(value)
    },
    // Called when ItemTraitSet changes so URL params stay in sync...
    updateURLQueryParamsFromTraitSet(
      itemTraitSet: IItemTraitSet,
      page: number,
    ) {
      const itemTraitSetToURLParamsMapper = {
        mailboxIDs: 'mailbox_ids',
        projectIDs: 'internal_project_ids',
        priorityIDs: 'priority_ids',
        statusIDs: 'status_ids',
        statusType: 'status_type',
        assigneeIDs: 'assignee_ids',
        assignedNotToYou: 'assigned_not_to_you',
        jobAppointeeGroupingsString: 'job_appointee_groupings_string',
        sort: 'sort',
        dueType: 'due_type',
        includingTagIDs: 'including_tag_ids',
        notIncludingTagIDs: 'not_including_tag_ids',
        startDueDate: 'start_due_date',
        endDueDate: 'end_due_date',
        searchTerm: 'search_term',
        onlyWatching: 'only_watching',
        createdWithinDays: 'created_within_days',
        lastUpdatedWithinDays: 'last_updated_within_days',
        creatorIDs: 'creator_ids',
        customerIDs: 'customer_ids',
        isSpam: 'is_spam',
      }

      const queryParams = mapKeys(itemTraitSet, (value: any, key: string) => {
        // @ts-ignore
        return itemTraitSetToURLParamsMapper[key]
      })

      // Default page to 1 if needed...
      queryParams.page = page ? page : 1

      // Clean null, FALSE, or undefined properties
      // That way, these won't appear in the url params all ugly-like
      // Weed out 'undefined' keys as those are potential legacy traits that
      // no longer exist.
      for (const key in queryParams) {
        if (
          key === 'undefined' ||
          queryParams[key] === null ||
          queryParams[key] === undefined ||
          queryParams[key] === false
        ) {
          delete queryParams[key]
        }
      }

      this.$router.replace({
        query: queryParams,
      })
    },

    /****************************************************/
    /* Breadcrumb / ItemTraitSet syncing methods             
    /****************************************************/
    // Called when breadcrumb nav item is updated to a new named filter
    updateItemTraitSetFromFilterID(id: string) {
      // Persist the old sort state...
      const persistSort = this.itemTraitSet.sort

      if (ItemFilterAssistant.isDefaultIdentifier(id)) {
        const defaultFilterID = ItemFilterAssistant.getIDOfFilterIdentifier(id)

        switch (defaultFilterID) {
          case DefaultFilterIDs.ACTIVE:
            this.itemTraitSet = cloneDeep(
              ItemFilterAssistant.getItemFilterTraitSet_Active(),
            )
            break
          case DefaultFilterIDs.ASSIGNED_TO_OTHERS:
            this.itemTraitSet = cloneDeep(
              ItemFilterAssistant.getItemTraitSet_AssignedToOthers(),
            )
            break
          case DefaultFilterIDs.ASSIGNED_TO_YOU:
            this.itemTraitSet = cloneDeep(
              ItemFilterAssistant.getItemTraitSet_AssignedToYou(
                this.$permissions.loggedInUser.id,
              ),
            )
            break
          case DefaultFilterIDs.CREATED_BY_YOU:
            this.itemTraitSet = cloneDeep(
              ItemFilterAssistant.getItemTraitSet_CreatedByYou(
                this.$permissions.loggedInUser.id,
              ),
            )
            break
          case DefaultFilterIDs.DUE_NOW:
            this.itemTraitSet = cloneDeep(
              ItemFilterAssistant.getItemTraitSet_DueNow(),
            )
            break
          case DefaultFilterIDs.EVERYTHING:
            this.itemTraitSet = cloneDeep(
              ItemFilterAssistant.getItemTraitSet_Everything(),
            )
            break
          case DefaultFilterIDs.INACTIVE:
            this.itemTraitSet = cloneDeep(
              ItemFilterAssistant.getItemTraitSet_Inactive(),
            )
            break
          case DefaultFilterIDs.SPAM:
            this.itemTraitSet = cloneDeep(
              ItemFilterAssistant.getItemTraitSet_Spam(),
            )
            break
          case DefaultFilterIDs.UNASSIGNED:
            this.itemTraitSet = cloneDeep(
              ItemFilterAssistant.getItemTraitSet_Unassigned(),
            )
            break
          case DefaultFilterIDs.YOURE_WATCHING:
            this.itemTraitSet = cloneDeep(
              ItemFilterAssistant.getItemTraitSet_Watching(),
            )
            break
        }
      }

      if (ItemFilterAssistant.isCustomIdentifier(id)) {
        const customFilterID = ItemFilterAssistant.getIDOfFilterIdentifier(id)
        const selectedCustomFilterItemTraitSet = this.customFilterItemTraitSets.find(
          c => c.id === customFilterID,
        )

        if (selectedCustomFilterItemTraitSet) {
          this.itemTraitSet = cloneDeep(
            selectedCustomFilterItemTraitSet.itemTraitSet,
          )
        }

        // If we cannot find the custom filter (bogus or deleted id,
        // don't set the item trait set...let it default to whatever it was,
        // i.e. the defaulItemTraitSet if called on postCreated() from URL.)
      }

      this.itemTraitSet.sort = persistSort
    },
    // Checks if the current itemTraitSet matches a default or custom filter
    syncCurrentFilterNavItemWithItemTraitSet() {
      // Don't run until defaultFilterNavItems loaded...
      if (this.defaultFilterNavItems.length === 0) {
        return
      }

      // Check: ACTIVE
      if (
        this.isItemTraitSetEquivalent(
          ItemFilterAssistant.getItemFilterTraitSet_Active(),
        )
      ) {
        this.currentFilterNavItem = this.getDefaultFilterNavItem(
          DefaultFilterIDs.ACTIVE,
        )
        return
      }

      // Check: ASSIGNED_TO_OTHERS
      if (
        this.isItemTraitSetEquivalent(
          ItemFilterAssistant.getItemTraitSet_AssignedToOthers(),
        )
      ) {
        this.currentFilterNavItem = this.getDefaultFilterNavItem(
          DefaultFilterIDs.ASSIGNED_TO_OTHERS,
        )
        return
      }

      // Check: ASSIGNED_TO_YOU
      if (
        this.isItemTraitSetEquivalent(
          ItemFilterAssistant.getItemTraitSet_AssignedToYou(
            this.$permissions.loggedInUser.id,
          ),
        )
      ) {
        this.currentFilterNavItem = this.getDefaultFilterNavItem(
          DefaultFilterIDs.ASSIGNED_TO_YOU,
        )
        return
      }

      // Check: CREATED_BY_YOU
      if (
        this.isItemTraitSetEquivalent(
          ItemFilterAssistant.getItemTraitSet_CreatedByYou(
            this.$permissions.loggedInUser.id,
          ),
        )
      ) {
        this.currentFilterNavItem = this.getDefaultFilterNavItem(
          DefaultFilterIDs.CREATED_BY_YOU,
        )
        return
      }

      // Check: DUE_NOW
      if (
        this.isItemTraitSetEquivalent(
          ItemFilterAssistant.getItemTraitSet_DueNow(),
        )
      ) {
        this.currentFilterNavItem = this.getDefaultFilterNavItem(
          DefaultFilterIDs.DUE_NOW,
        )
        return
      }

      // Check: EVERYTHING
      if (
        this.isItemTraitSetEquivalent(
          ItemFilterAssistant.getItemTraitSet_Everything(),
        )
      ) {
        this.currentFilterNavItem = this.getDefaultFilterNavItem(
          DefaultFilterIDs.EVERYTHING,
        )
        return
      }

      // Check: INACTIVE
      if (
        this.isItemTraitSetEquivalent(
          ItemFilterAssistant.getItemTraitSet_Inactive(),
        )
      ) {
        this.currentFilterNavItem = this.getDefaultFilterNavItem(
          DefaultFilterIDs.INACTIVE,
        )
        return
      }

      // Check: SPAM
      if (
        this.isItemTraitSetEquivalent(
          ItemFilterAssistant.getItemTraitSet_Spam(),
        )
      ) {
        this.currentFilterNavItem = this.getDefaultFilterNavItem(
          DefaultFilterIDs.SPAM,
        )
        return
      }

      // Check: UNASSIGNED
      if (
        this.isItemTraitSetEquivalent(
          ItemFilterAssistant.getItemTraitSet_Unassigned(),
        )
      ) {
        this.currentFilterNavItem = this.getDefaultFilterNavItem(
          DefaultFilterIDs.UNASSIGNED,
        )
        return
      }

      // Check: YOURE_WATCHING
      if (
        this.isItemTraitSetEquivalent(
          ItemFilterAssistant.getItemTraitSet_Watching(),
        )
      ) {
        this.currentFilterNavItem = this.getDefaultFilterNavItem(
          DefaultFilterIDs.YOURE_WATCHING,
        )
        return
      }

      // Check: Custom filters...
      const cfMatch = this.customFilterItemTraitSets.find(c =>
        this.isItemTraitSetEquivalent(c.itemTraitSet),
      )

      if (cfMatch) {
        this.currentFilterNavItem = this.getCustomFilterNavItem(cfMatch.id)
        return
      }

      // Otherwise, default the filter
      this.currentFilterNavItem = ItemFilterAssistant.getEmptyFilterNavItem()
      this.currentFilterNavItem.label = TextAssistant.itemPluralName(
        this.isAllConvos ? EEntities.MAILBOXES : EEntities.PROJECTS,
      )
    },
    getDefaultFilterNavItem(id: number): INavMenuItem {
      return this.defaultFilterNavItems.find(
        f => f.value === ItemFilterAssistant.getDefaultFilterIdentifier(id),
      )
    },
    getCustomFilterNavItem(id: number): INavMenuItem {
      return this.customFilterNavItems.find(
        f => f.value === ItemFilterAssistant.getCustomFilterIdentifier(id),
      )
    },

    /****************************************************/
    /* Bulk edit methods            
    /****************************************************/
    updateSelectedRows(ids: number[]) {
      this.selectedItemIDs = ids
    },

    /****************************************************/
    /* Event handling methods on listing                
    /****************************************************/
    resetItemTraitSet() {
      // Persist the old sort state...
      const persistSort = this.itemTraitSet.sort
      this.itemTraitSet = cloneDeep(DefaultItemTraitSet)
      this.itemTraitSet.sort = persistSort
    },
    changePage(page: number) {
      this.currentPage = page
      this.updateURLAndFetchResults()
    },
  },
})
